// @ts-check
// Editor de opções de SELECT e MULTI_SELECT (doc 11, 3.6.6; doc 04, 2.4): arrastar para reordenar, cor, rótulo,
// opção padrão e remoção. Também serve às etapas do funil, com etapas protegidas e ordem fixa nas extremidades.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { computeUniqueOptionValue } from '../../../../../shared/metadata/metadata-names.js';
import { MAIN_COLOR_NAMES } from '../../../lib/theme-colors.js';
import { generateId } from '../../../lib/utils.js';
import { Button, LightIconButton } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { t, translateMetadataLabel } from '../../../i18n/index.js';
import { ColorPicker } from './pickers.js';

/**
 * Opção em edição: `id` só nas que já existem; `clientKey` identifica a linha durante a edição.
 * @typedef {{ clientKey: string, id?: string, value: string, label: string, color: string }} DraftOption
 */

/**
 * @param {Array<{ id?: string, value: string, label: string, color?: string, position?: number }> | null | undefined} options
 * @returns {DraftOption[]}
 */
export function toDraftOptions(options) {
  return [...(options ?? [])]
    .sort((first, second) => (first.position ?? 0) - (second.position ?? 0))
    .map((option) => ({ clientKey: option.id ?? generateId(), id: option.id, value: option.value, label: translateMetadataLabel(option.label), color: option.color ?? 'gray' }));
}

/**
 * Forma enviada à API (posição = ordem da lista).
 * @param {DraftOption[]} draftOptions
 */
export function toApiOptions(draftOptions) {
  return draftOptions.map((option, position) => ({ ...(option.id ? { id: option.id } : {}), value: option.value, label: option.label.trim(), color: option.color, position }));
}

/**
 * Opções que existiam e saíram da lista.
 * @param {Array<{ id: string, value: string, label: string, color?: string }>} originalOptions @param {DraftOption[]} draftOptions
 */
export function findRemovedOptions(originalOptions, draftOptions) {
  return originalOptions.filter((option) => !draftOptions.some((draft) => draft.id === option.id));
}

/**
 * "12 registros"; zero vira "Sem registros" (no pt-BR o plural de 0 cairia em "0 registro").
 * @param {number} count
 */
export const formatOptionUsage = (count) => (count === 0 ? t('dataModel.options.unused') : t('dataModel.options.usage', { count }));

/**
 * Primeiro problema da lista (para desabilitar o salvar com a mensagem certa), ou null.
 * @param {DraftOption[]} draftOptions
 */
export function validateDraftOptions(draftOptions) {
  if (draftOptions.length === 0) return t('dataModel.options.errors.empty');
  const labels = new Set();
  for (const option of draftOptions) {
    const label = option.label.trim();
    if (label === '') return t('dataModel.options.errors.emptyLabel');
    if (label.includes(',')) return t('dataModel.options.errors.comma', { label });
    if (labels.has(label.toLowerCase())) return t('dataModel.options.errors.duplicated', { label });
    labels.add(label.toLowerCase());
  }
  return null;
}

/**
 * @param {{ options: DraftOption[], onChange: (options: DraftOption[]) => void, usage?: Record<string, number> | null,
 *   usageLabel?: (count: number, value: string) => string, defaultValue?: string | null, onDefaultChange?: ((value: string | null) => void) | null,
 *   protectedValues?: ReadonlyArray<string>, arrangeOptions?: (options: DraftOption[]) => DraftOption[], disabled?: boolean,
 *   addLabel?: string }} props
 */
export function SelectOptionsEditor({
  options, onChange, usage = null, usageLabel, defaultValue = null, onDefaultChange = null, protectedValues = [],
  arrangeOptions = (list) => list, disabled = false, addLabel,
}) {
  const [draggedKey, setDraggedKey] = useState(/** @type {string | null} */ (null));
  const [dropTargetKey, setDropTargetKey] = useState(/** @type {string | null} */ (null));
  const listRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  /** @param {DraftOption[]} next */
  const commit = (next) => onChange(arrangeOptions(next));

  /** @param {string} clientKey @param {Partial<DraftOption>} patch */
  const updateOption = (clientKey, patch) => {
    onChange(options.map((option) => {
      if (option.clientKey !== clientKey) return option;
      const next = { ...option, ...patch };
      // opção nova acompanha o rótulo no valor de API; as existentes mantêm o valor (registros já o usam)
      if (patch.label !== undefined && !option.id) {
        next.value = computeUniqueOptionValue(patch.label, options.filter((other) => other.clientKey !== clientKey).map((other) => other.value));
      }
      return next;
    }));
  };

  /** @param {string} sourceKey @param {string} targetKey */
  const moveOption = (sourceKey, targetKey) => {
    if (sourceKey === targetKey) return;
    const source = options.find((option) => option.clientKey === sourceKey);
    if (!source) return;
    const remaining = options.filter((option) => option.clientKey !== sourceKey);
    const targetIndex = remaining.findIndex((option) => option.clientKey === targetKey);
    const originalTargetIndex = options.findIndex((option) => option.clientKey === targetKey);
    const sourceIndex = options.findIndex((option) => option.clientKey === sourceKey);
    // arrastar para baixo solta depois do alvo; para cima, antes
    const insertAt = sourceIndex < originalTargetIndex ? targetIndex + 1 : targetIndex;
    remaining.splice(insertAt, 0, source);
    commit(remaining);
  };

  /** @param {number} index @param {number} direction */
  const moveByKeyboard = (index, direction) => {
    const target = options[index + direction];
    if (target) moveOption(options[index].clientKey, target.clientKey);
  };

  const addOption = () => {
    const lastColor = options.at(-1)?.color ?? 'gray';
    const color = MAIN_COLOR_NAMES[(MAIN_COLOR_NAMES.indexOf(lastColor) + 3) % MAIN_COLOR_NAMES.length];
    const label = t('dataModel.options.newOptionLabel', { number: options.length + 1 });
    const value = computeUniqueOptionValue(label, options.map((option) => option.value));
    const clientKey = generateId();
    commit([...options, { clientKey, value, label, color }]);
    requestAnimationFrame(() => {
      /** @type {HTMLInputElement | null | undefined} */ (listRef.current?.querySelector(`[data-option-key="${clientKey}"] input`))?.select();
    });
  };

  return html`<div class="data-model-options">
    <div class="data-model-options-list" ref=${listRef} role="list">
      ${options.map((option, index) => {
        const isProtected = protectedValues.includes(option.value);
        const count = usage && option.id ? usage[option.value] ?? 0 : null;
        const isDefault = defaultValue === option.value;
        return html`<div key=${option.clientKey} role="listitem" class="data-model-option-row" data-option-key=${option.clientKey}
          data-dragging=${draggedKey === option.clientKey ? 'true' : undefined}
          data-drop-target=${dropTargetKey === option.clientKey && draggedKey !== option.clientKey ? 'true' : undefined}
          onDragOver=${(/** @type {DragEvent} */ event) => {
            if (!draggedKey) return;
            event.preventDefault();
            setDropTargetKey(option.clientKey);
          }}
          onDrop=${(/** @type {DragEvent} */ event) => {
            event.preventDefault();
            if (draggedKey) moveOption(draggedKey, option.clientKey);
            setDraggedKey(null);
            setDropTargetKey(null);
          }}
          onDragEnd=${() => {
            setDraggedKey(null);
            setDropTargetKey(null);
          }}>
          <button type="button" class="data-model-option-grip" disabled=${disabled || isProtected} aria-label=${t('dataModel.options.reorder', { label: option.label })}
            title=${isProtected ? t('dataModel.funnel.protectedHint') : t('dataModel.options.reorderHint')} draggable=${disabled || isProtected ? 'false' : 'true'}
            onDragStart=${(/** @type {DragEvent} */ event) => {
              setDraggedKey(option.clientKey);
              const row = /** @type {HTMLElement} */ (event.currentTarget).closest('.data-model-option-row');
              if (event.dataTransfer) {
                event.dataTransfer.setData('text/plain', option.clientKey);
                event.dataTransfer.effectAllowed = 'move';
                if (row) event.dataTransfer.setDragImage(row, 16, 16);
              }
            }}
            onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
              if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault();
                moveByKeyboard(index, event.key === 'ArrowUp' ? -1 : 1);
              }
            }}><${Icon} name="IconGripVertical" size=${14} /></button>
          <${ColorPicker} value=${option.color} disabled=${disabled} ariaLabel=${t('dataModel.options.colorOf', { label: option.label })}
            onChange=${(color) => updateOption(option.clientKey, { color })} />
          <input class="text-input data-model-option-label" value=${option.label} disabled=${disabled}
            aria-label=${t('dataModel.options.labelOf', { number: index + 1 })} maxlength="63"
            onInput=${(event) => updateOption(option.clientKey, { label: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
          <span class="data-model-option-value" title=${t('dataModel.options.apiValue')}>${option.value}</span>
          <span class="data-model-option-usage">${count === null ? '' : usageLabel ? usageLabel(count, option.value) : formatOptionUsage(count)}</span>
          ${onDefaultChange
            ? html`<${LightIconButton} icon=${isDefault ? 'IconStarFilled' : 'IconStar'} accent=${isDefault ? 'secondary' : 'tertiary'} active=${isDefault}
                disabled=${disabled} ariaLabel=${isDefault ? t('dataModel.options.removeDefault') : t('dataModel.options.setDefault')}
                onClick=${() => onDefaultChange(isDefault ? null : option.value)} />`
            : null}
          ${isProtected
            ? html`<span class="data-model-option-lock" title=${t('dataModel.funnel.protectedHint')} aria-label=${t('dataModel.funnel.protectedHint')}><${Icon} name="IconLock" size=${14} /></span>`
            : html`<${LightIconButton} icon="IconTrash" accent="tertiary" disabled=${disabled || options.length <= 1}
                ariaLabel=${t('dataModel.options.remove', { label: option.label })}
                onClick=${() => onChange(options.filter((candidate) => candidate.clientKey !== option.clientKey))} />`}
        </div>`;
      })}
    </div>
    ${disabled ? null : html`<div><${Button} title=${addLabel ?? t('dataModel.options.add')} icon="IconPlus" variant="secondary" size="small" onClick=${addOption} /></div>`}
  </div>`;
}
